/**
 * AcceptInvitationPage - landing du lien d'invitation (utilisateur secondaire).
 * URL : /auth/accept-invitation?token=xxx
 *
 * Flow :
 *  1. Token depuis la query string
 *  2. L'invite choisit son mot de passe (>= 10 caracteres)
 *  3. POST /api/auth/accept-invitation { token, newPassword } -> status active
 *  4. Succes : bouton vers /login. (Le login exige ensuite un code OTP email.)
 */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Body1, Button, Input, Label, Spinner, Title2, tokens,
} from '@fluentui/react-components';
import { authApi } from '../auth/authApi';
import { BrandLogo } from '../components/BrandLogo';

const MIN = 10;
type State = 'form' | 'ok' | 'error';

export function AcceptInvitationPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [state, setState] = useState<State>('form');
  const [alreadyActive, setAlreadyActive] = useState(false);
  const [pwd, setPwd]       = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]   = useState<string | null>(null);

  const tooShort = pwd.length > 0 && pwd.length < MIN;
  const mismatch = confirm.length > 0 && confirm !== pwd;
  const canSubmit = pwd.length >= MIN && confirm === pwd && !!token;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const r = await authApi.acceptInvitation(token, pwd);
      setAlreadyActive(Boolean(r.alreadyActive));
      setState('ok');
    } catch (err) {
      setError((err as Error)?.message ?? t('auth.acceptInvitation.errorExpired', 'Lien invalide ou expiré.'));
      setState('error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `linear-gradient(135deg, ${tokens.colorNeutralBackground2} 0%, ${tokens.colorNeutralBackground3} 100%)`,
      padding: 24,
    }}>
      <div style={{
        width: '100%', maxWidth: 460, padding: '44px 32px', textAlign: 'center',
        background: tokens.colorNeutralBackground1, borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)', border: `1px solid ${tokens.colorNeutralStroke2}`,
      }}>
        <BrandLogo height={56} />

        {!token && (
          <>
            <Title2 as="h1" style={{ marginTop: 24, color: tokens.colorPaletteRedForeground1 }}>
              ✕ {t('auth.acceptInvitation.errorTitle', 'Invitation invalide')}
            </Title2>
            <Body1 block style={{ marginTop: 12, color: tokens.colorNeutralForeground2 }}>
              {t('auth.acceptInvitation.errorExpired', 'Ce lien d’invitation est invalide ou a expiré.')}
            </Body1>
            <Button as="a" appearance="primary" style={{ marginTop: 24 }}>
              <Link to="/login" style={{ color: 'inherit', textDecoration: 'none' }}>
                {t('auth.acceptInvitation.goToLogin', 'Aller à la connexion')}
              </Link>
            </Button>
          </>
        )}

        {token && state === 'form' && (
          <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 24, textAlign: 'left' }}>
            <Title2 as="h1" style={{ textAlign: 'center' }}>
              {t('auth.acceptInvitation.chooseTitle', 'Bienvenue — choisissez votre mot de passe')}
            </Title2>
            <Body1 style={{ color: tokens.colorNeutralForeground3, textAlign: 'center' }}>
              {t('auth.acceptInvitation.chooseSubtitle', 'Définissez un mot de passe d’au moins 10 caractères pour finaliser votre inscription.')}
            </Body1>
            <div>
              <Label htmlFor="np" required>{t('auth.reset.newLabel', 'Nouveau mot de passe')}</Label>
              <Input id="np" type="password" required value={pwd}
                onChange={(_e, d) => setPwd(d.value)} style={{ width: '100%', marginTop: 4 }} />
              {tooShort && (
                <Body1 style={{ color: tokens.colorPaletteRedForeground2, fontSize: 12 }}>
                  {t('auth.reset.tooShort', 'Au moins 10 caractères.')}
                </Body1>
              )}
            </div>
            <div>
              <Label htmlFor="cp" required>{t('auth.reset.confirmLabel', 'Confirmer le mot de passe')}</Label>
              <Input id="cp" type="password" required value={confirm}
                onChange={(_e, d) => setConfirm(d.value)} style={{ width: '100%', marginTop: 4 }} />
              {mismatch && (
                <Body1 style={{ color: tokens.colorPaletteRedForeground2, fontSize: 12 }}>
                  {t('auth.reset.mismatch', 'Les mots de passe ne correspondent pas.')}
                </Body1>
              )}
            </div>
            {error && (
              <div style={{
                padding: 12, background: tokens.colorPaletteRedBackground1,
                borderLeft: `4px solid ${tokens.colorPaletteRedBorder2}`, borderRadius: 6,
                fontSize: 13, color: tokens.colorPaletteRedForeground2,
              }}>{error}</div>
            )}
            <Button type="submit" appearance="primary" size="large"
              disabled={submitting || !canSubmit}
              icon={submitting ? <Spinner size="tiny" /> : undefined}>
              {submitting ? t('auth.reset.submitting', 'Enregistrement…') : t('auth.acceptInvitation.chooseSubmit', 'Créer mon compte')}
            </Button>
          </form>
        )}

        {token && state === 'ok' && (
          <>
            <Title2 as="h1" style={{ marginTop: 24, color: tokens.colorPaletteGreenForeground1 }}>
              ✓ {t('auth.acceptInvitation.successTitle', 'Compte activé')}
            </Title2>
            <Body1 block style={{ marginTop: 16, color: tokens.colorNeutralForeground2 }}>
              {alreadyActive
                ? t('auth.acceptInvitation.alreadyActive', 'Votre compte est déjà actif. Connectez-vous avec votre email et votre mot de passe.')
                : t('auth.acceptInvitation.successBody', 'Vous pouvez maintenant vous connecter avec votre email et votre mot de passe (un code de vérification vous sera envoyé par email).')}
            </Body1>
            <Button as="a" appearance="primary" size="large" style={{ marginTop: 28 }}>
              <Link to="/login" style={{ color: 'inherit', textDecoration: 'none' }}>
                {t('auth.acceptInvitation.goToLogin', 'Aller à la connexion')}
              </Link>
            </Button>
          </>
        )}

        {token && state === 'error' && (
          <>
            <Title2 as="h1" style={{ marginTop: 24, color: tokens.colorPaletteRedForeground1 }}>
              ✕ {t('auth.acceptInvitation.errorTitle', 'Invitation invalide')}
            </Title2>
            <Body1 block style={{ marginTop: 12, color: tokens.colorNeutralForeground2 }}>
              {error}
            </Body1>
            <Button appearance="secondary" style={{ marginTop: 20 }} onClick={() => { setState('form'); setError(null); }}>
              {t('auth.otp.back', 'Retour')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
